
<div class="mb-3">
    <app-breadcrumbs [items]="breadcrumbItems()" />
</div>


<div class="mx-auto w-full max-w-5xl p-4 sm:p-6">
  @if (clientLoading()) {
    <div class="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
      <div class="text-sm text-zinc-600">Loading client details...</div>
    </div>
  } @else if (errorMessage()) {
    <div class="space-y-4">
      <div class="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 shadow-sm">
        {{ errorMessage() }}
      </div>

      <button
        type="button"
        (click)="goBackHome()"
        class="inline-flex items-center justify-center rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-800 transition hover:bg-zinc-50"
      >
        Back to Home
      </button>
    </div>
  } @else if (client(); as currentClient) {
    <div class="space-y-6">
      <div class="relative rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-6">
        <div class="mb-4 flex justify-end sm:absolute sm:right-6 sm:top-6 sm:mb-0">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</span>

            @if (canToggleClientStatus()) {
              <div
                class="status-segmented"
                [class.is-open]="currentClient.status === 'open'"
                [class.is-closed]="currentClient.status === 'closed'"
                [class.is-updating]="statusUpdating()"
              >
                <span class="status-segmented__thumb" aria-hidden="true"></span>
                <button
                  type="button"
                  (click)="setClientStatus('open')"
                  [disabled]="statusUpdating() || currentClient.status === 'open'"
                  [class.is-active]="currentClient.status === 'open'"
                  class="status-segmented__button"
                >
                  Open
                </button>

                <button
                  type="button"
                  (click)="setClientStatus('closed')"
                  [disabled]="statusUpdating() || currentClient.status === 'closed'"
                  [class.is-active]="currentClient.status === 'closed'"
                  class="status-segmented__button"
                >
                  Closed
                </button>
              </div>
            } @else {
              <span
                class="status-pill rounded-full px-2.5 py-1 text-xs font-medium"
                [class.status-pill-open]="currentClient.status === 'open'"
              >
                {{ currentClient.status === 'open' ? 'Open' : 'Closed' }}
              </span>
            }
          </div>
        </div>

        <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div class="min-w-0 sm:pr-72">
            <div class="text-sm font-medium uppercase tracking-wide text-zinc-500">
              Client
            </div>

            <h1 class="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl">
              {{ currentClient.fullName }}
            </h1>

            <div class="mt-3 flex flex-wrap items-center gap-2 text-sm text-zinc-600">
              <span class="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 font-medium text-zinc-700">
                Client ID:
                <span class="font-mono text-zinc-900">{{ currentClient.clientUniqueId || 'Not Assigned' }}</span>
                @if (currentClient.clientUniqueId) {
                  <button
                    type="button"
                    (click)="copyClientUniqueId()"
                    class="inline-flex items-center justify-center rounded-md border border-transparent bg-transparent p-1 text-zinc-600 transition hover:border-zinc-300 hover:text-zinc-800 focus-visible:border-zinc-300 focus-visible:outline-none"
                    aria-label="Copy client ID"
                    title="Copy client ID"
                  >
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M9 9.75A2.25 2.25 0 0 1 11.25 7.5h6A2.25 2.25 0 0 1 19.5 9.75v6A2.25 2.25 0 0 1 17.25 18h-6A2.25 2.25 0 0 1 9 15.75v-6Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M15 7.5V6.75A2.25 2.25 0 0 0 12.75 4.5h-6A2.25 2.25 0 0 0 4.5 6.75v6A2.25 2.25 0 0 0 6.75 15H9"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                }
              </span>

              <span class="rounded-full bg-zinc-100 px-3 py-1 font-medium text-zinc-700">
                Birthdate: {{ currentClient.birthdate | date:'MMM d, y':'UTC' }}
              </span>
            </div>

            <div class="mt-3 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
              <button
                type="button"
                (click)="openProfile()"
                class="inline-flex items-center justify-center rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-medium text-zinc-800 transition hover:bg-zinc-50"
              >
                Client Profile
              </button>
                
              <button
                type="button"
                (click)="createCase()"
                class="inline-flex items-center justify-center rounded-xl bg-zinc-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-zinc-800"
              >
                Create Case
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div class="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Open Cases
          </div>
          <div class="mt-2 text-2xl font-semibold text-zinc-900">
            {{ openCaseCount() }}
          </div>
        </div>

        <div class="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Closed Cases
          </div>
          <div class="mt-2 text-2xl font-semibold text-zinc-900">
            {{ closedCaseCount() }}
          </div>
        </div>

        <div class="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Viewing
          </div>
          <div class="mt-2 text-base font-semibold text-zinc-900">
            {{ activeTab() === 'open' ? 'Open Cases' : 'Case History' }}
          </div>
        </div>
      </div>

      <div class="rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="flex items-center gap-2 border-b border-zinc-200 px-4 py-3 sm:px-6">
          <button
            type="button"
            (click)="setTab('open')"
            class="segmented-option rounded-xl px-4 py-2 text-sm font-medium transition"
            [class.segmented-option-selected]="activeTab() === 'open'"
          >
            Open Cases
          </button>

          <button
            type="button"
            (click)="setTab('history')"
            class="segmented-option rounded-xl px-4 py-2 text-sm font-medium transition"
            [class.segmented-option-selected]="activeTab() === 'history'"
          >
            Case History
          </button>
        </div>

        <div class="p-4 sm:p-6">
            @if (casesLoading()) {
                <div class="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
                    Loading cases...
                </div>
            } @else if (displayedCases().length === 0) {
            <div class="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
              <div class="text-base font-medium text-zinc-900">
                No {{ activeTab() === 'open' ? 'open' : 'closed' }} cases found
              </div>
              <p class="mt-2 text-sm text-zinc-600">
                {{ activeTab() === 'open'
                  ? 'Create a new case to get started for this client.'
                  : 'This client does not have any closed cases yet.' }}
              </p>

              @if (activeTab() === 'open') {
                <button
                  type="button"
                  (click)="createCase()"
                  class="mt-4 inline-flex items-center justify-center rounded-xl bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-zinc-800"
                >
                  Create Case
                </button>
              }
            </div>
          } @else {
            <div class="space-y-4">
              @for (caseItem of displayedCases(); track trackCaseById($index, caseItem)) {
                <button
                  type="button"
                  (click)="openCase(caseItem.id)"
                  class="group block w-full rounded-2xl border border-zinc-200 bg-white p-4 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-zinc-400 hover:shadow-lg focus-visible:-translate-y-0.5 focus-visible:border-zinc-400 focus-visible:shadow-lg focus-visible:outline-none sm:p-5"
                >
                  <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <div class="text-base font-semibold text-zinc-900">
                          Case {{ caseItem.caseUniqueId }}
                        </div>

                        <span
                          class="status-pill rounded-full px-2.5 py-1 text-xs font-medium"
                          [class.status-pill-open]="caseItem.status === 'open'"
                        >
                          {{ getCaseStatusLabel(caseItem.status) }}
                        </span>
                      </div>

                      <div class="mt-2 grid grid-cols-1 gap-2 text-sm text-zinc-600 sm:grid-cols-2">
                        <div>
                          <span class="font-medium text-zinc-800">Service:</span>
                          {{ caseItem.serviceName }}
                        </div>
                        <div>
                          <span class="font-medium text-zinc-800">Partner:</span>
                          {{ caseItem.partnerName }}
                        </div>
                        <div>
                          <span class="font-medium text-zinc-800">Opened:</span>
                          {{ caseItem.dateOpened }}
                        </div>
                        <div>
                          <span class="font-medium text-zinc-800">Closed:</span>
                          {{ caseItem.closeDate || '—' }}
                        </div>
                        <div>
                          <span class="font-medium text-zinc-800">Referral Source:</span>
                          {{ caseItem.referralSource }}
                        </div>
                        <div>
                          <span class="font-medium text-zinc-800">Referral Date:</span>
                          {{ caseItem.referralDate }}
                        </div>
                      </div>
                    </div>

                    <div class="shrink-0 self-center text-sm font-semibold text-zinc-700 transition group-hover:text-zinc-900 group-focus-visible:text-zinc-900 md:text-right">
                      Open Case <span aria-hidden="true" class="inline-block transition-transform group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5">→</span>
                    </div>
                  </div>
                </button>
              }
            </div>
          }
        </div>
      </div>

      @if (pendingStatusChange() === 'closed') {
        <div class="fixed inset-0 z-40 bg-slate-900/50" (click)="cancelCloseClient()"></div>

        <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="close-client-title">
          <article class="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
            <h3 id="close-client-title" class="text-lg font-semibold text-slate-900">Close Client?</h3>

            <p class="mt-2 text-sm text-slate-600">
              <span class="font-semibold text-slate-900">{{ currentClient.fullName }}</span>
              will be marked as closed. This does not delete the client record and you can reopen the client later.
            </p>

            <div class="mt-5 flex justify-end gap-2">
              <button
                type="button"
                class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                (click)="cancelCloseClient()"
                [disabled]="statusUpdating()"
              >
                Cancel
              </button>

              <button
                type="button"
                class="rounded-xl bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
                (click)="confirmCloseClient()"
                [disabled]="statusUpdating()"
              >
                Confirm Close
              </button>
            </div>
          </article>
        </div>
      }
    </div>
  }
</div>